/**
 * VIP 信息 Hook
 * 对标 tv-linux 项目的 userVIPInfo 函数
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getUserVIPInfo } from '@/services/userApi';
import { getCookieAmend, setCookieAmend } from '@/utils/apiUtils';
import { formatDate } from '@/utils/accountUtils';

export interface VipInfo {
  /** 会员状态: 1=游客, 2=登录状态非会员, 3=会员 */
  status: number;
  /** 会员到期时间（格式化后的日期字符串 YYYY-MM-DD） */
  endDate: string;
  /** 原始到期时间戳 */
  endTimestamp: number;
  /** 是否是有效会员 */
  isVip: boolean;
  /** 后台返回的提示文案（用于非会员提示） */
  tips?: string;
}

interface UseVipInfoReturn {
  vipInfo: VipInfo | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * 获取并管理用户 VIP 信息
 */
export function useVipInfo(): UseVipInfoReturn {
  const [vipInfo, setVipInfo] = useState<VipInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 防止 StrictMode 下重复请求
  const hasFetchedRef = useRef(false);

  const fetchVipInfo = useCallback(async (force = false) => {
    // 检查是否已登录
    const isLogin = getCookieAmend('isLogin');
    if (isLogin !== 'true') {
      setLoading(false);
      setVipInfo(null);
      return;
    }

    // 防止重复请求（除非强制刷新）
    if (!force && hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    setLoading(true);
    setError(null);

    try {
      const body = {
        guid: getCookieAmend('guid'),
        vplatform: getCookieAmend('vplatform'),
        vuid: getCookieAmend('vuserid'),
      };

      const res = await getUserVIPInfo(body);

      if (res?.ret === 0) {
        const currentData = res?.data;
        if (!currentData?.code && !currentData?.errCode && !currentData?.err_code) {
          const endTimestamp = currentData.end || 0;
          const endDate = endTimestamp ? formatDate(endTimestamp) : '';
          const status = currentData.status || 0;
          const tips = currentData.tips || '';

          // 设置 cookie（对标 tv-linux）
          setCookieAmend('status', String(status));
          if (endDate) {
            setCookieAmend('end', endDate);
          }

          setVipInfo({
            status,
            endDate,
            endTimestamp,
            isVip: status === 3, // status: 1=游客, 2=登录非会员, 3=会员
            tips,
          });
        } else {
          console.error('[useVipInfo] API error:', currentData);
          setError(currentData?.msg || 'Failed to get VIP info');
        }
      } else {
        console.error('[useVipInfo] Request failed:', res);
        setError(res?.msg || 'Request failed');
      }
    } catch (err) {
      console.error('[useVipInfo] Exception:', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化时获取 VIP 信息
  useEffect(() => {
    // 先尝试从 cookie 读取缓存的 VIP 信息
    const cachedStatus = getCookieAmend('status');
    const cachedEnd = getCookieAmend('end');

    if (cachedStatus) {
      const statusNum = parseInt(cachedStatus, 10);
      setVipInfo({
        status: statusNum,
        endDate: cachedEnd,
        endTimestamp: 0,
        isVip: statusNum === 3, // status: 1=游客, 2=登录非会员, 3=会员
        tips: '', // 缓存中没有tips，等待异步刷新
      });
    }

    // 然后异步刷新
    fetchVipInfo();
  }, [fetchVipInfo]);

  // 手动刷新时强制请求
  const refresh = useCallback(async () => {
    await fetchVipInfo(true);
  }, [fetchVipInfo]);

  return {
    vipInfo,
    loading,
    error,
    refresh,
  };
}
